import axios from 'axios';

const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
const region = process.env.REACT_APP_COGNITO_REGION || 'us-west-2';
const storageKey = 'bomwise.account';
const SESSION_DURATION = 60 * 24 * 60 * 60 * 1000;
const LEGACY_DURATION = 7 * 24 * 60 * 60 * 1000;
const INVALID_SESSION_CODES = new Set([
  'NotAuthorizedException', 'UserNotFoundException', 'UserNotConfirmedException', 'PasswordResetRequiredException'
]);
let refreshing;

export function account() {
  try {
    let value = JSON.parse(localStorage.getItem(storageKey));
    if (!value) {
      const legacy = JSON.parse(sessionStorage.getItem(storageKey));
      if (legacy?.idToken && legacy?.refreshToken) {
        let startedAt = Date.now();
        try {
          const payload = legacy.idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
          const claims = JSON.parse(atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '=')));
          if (Number.isFinite(claims.auth_time)) startedAt = claims.auth_time * 1000;
        } catch { /* Older opaque tokens retain their server-enforced lifetime. */ }
        value = { ...legacy, clientId, sessionExpiresAt: legacy.sessionExpiresAt || startedAt + LEGACY_DURATION };
        localStorage.setItem(storageKey, JSON.stringify(value));
      }
    }
    // Every upgraded tab discards its old private copy, even if another tab
    // already migrated the account into shared persistent storage.
    sessionStorage.removeItem(storageKey);
    if (!value) return null;
    if (value.clientId !== clientId || !value.idToken || !value.refreshToken
      || !Number.isFinite(value.sessionExpiresAt) || value.sessionExpiresAt <= Date.now()) {
      localStorage.removeItem(storageKey);
      return null;
    }
    return value;
  } catch { return null; }
}

function save(value) {
  if (value) localStorage.setItem(storageKey, JSON.stringify(value));
  else localStorage.removeItem(storageKey);
  sessionStorage.removeItem(storageKey);
  window.dispatchEvent(new Event('bomwise-auth'));
}

async function cognito(action, data) {
  if (!clientId) throw new Error('Account setup is missing. Configure REACT_APP_COGNITO_CLIENT_ID.');
  try {
    const response = await axios.post(`https://cognito-idp.${region}.amazonaws.com/`, data, {
      headers: { 'Content-Type': 'application/x-amz-json-1.1', 'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}` }
    });
    return response.data;
  } catch (error) {
    const message = error.response?.data?.message || error.message || 'Account request failed.';
    const failure = new Error(message);
    failure.code = error.response?.data?.__type?.split('#').pop();
    throw failure;
  }
}

function session(result, email, previous) {
  return {
    clientId, email, idToken: result.IdToken, accessToken: result.AccessToken,
    refreshToken: result.RefreshToken || previous?.refreshToken,
    expiresAt: Date.now() + result.ExpiresIn * 1000,
    // Refreshing JWTs must not turn a 60-day session into an unlimited session.
    sessionExpiresAt: previous?.sessionExpiresAt || Date.now() + SESSION_DURATION
  };
}

export const authService = {
  async register(email, password) {
    return cognito('SignUp', { ClientId: clientId, Username: email.trim().toLowerCase(), Password: password,
      UserAttributes: [{ Name: 'email', Value: email.trim().toLowerCase() }] });
  },
  async confirm(email, code) {
    return cognito('ConfirmSignUp', { ClientId: clientId, Username: email.trim().toLowerCase(), ConfirmationCode: code.trim() });
  },
  async resend(email) {
    return cognito('ResendConfirmationCode', { ClientId: clientId, Username: email.trim().toLowerCase() });
  },
  async login(email, password) {
    const normalized = email.trim().toLowerCase();
    const result = await cognito('InitiateAuth', { ClientId: clientId, AuthFlow: 'USER_PASSWORD_AUTH',
      AuthParameters: { USERNAME: normalized, PASSWORD: password } });
    if (!result.AuthenticationResult) throw new Error('This account requires an additional sign-in challenge. Contact your administrator.');
    save(session(result.AuthenticationResult, normalized));
  },
  async token({ forceRefresh = false, rejectedToken } = {}) {
    const current = account();
    if (!current) return null;
    // A delayed 401 for an older JWT must not force another refresh of a new one.
    if ((!forceRefresh || (rejectedToken && rejectedToken !== current.idToken))
      && current.expiresAt > Date.now() + 60000) return current.idToken;
    if (!refreshing || refreshing.refreshToken !== current.refreshToken) {
      const pending = { refreshToken: current.refreshToken };
      pending.promise = cognito('InitiateAuth', { ClientId: clientId, AuthFlow: 'REFRESH_TOKEN_AUTH',
        AuthParameters: { REFRESH_TOKEN: current.refreshToken } })
        .then(result => {
          // Do not restore a session if the user logged out while refresh was in flight.
          if (account()?.refreshToken !== current.refreshToken) return null;
          const next = session(result.AuthenticationResult, current.email, current);
          save(next);
          return next.idToken;
        })
        .catch(error => {
          if (INVALID_SESSION_CODES.has(error.code) && account()?.refreshToken === current.refreshToken) save(null);
          // Network errors, throttling and service outages do not invalidate a session.
          throw error;
        })
        .finally(() => { if (refreshing === pending) refreshing = null; });
      refreshing = pending;
    }
    return refreshing.promise;
  },
  clear(expectedToken) {
    if (expectedToken && account()?.idToken !== expectedToken) return;
    save(null);
  },
  async logout() {
    const current = account();
    save(null);
    // JWTs already issued can remain valid until their 15-minute expiry.
    if (current?.refreshToken) {
      try { await cognito('RevokeToken', { ClientId: clientId, Token: current.refreshToken }); }
      catch { /* Local sign-out still succeeds when offline. */ }
    }
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
  },
  async forgot(email) {
    return cognito('ForgotPassword', { ClientId: clientId, Username: email.trim().toLowerCase() });
  },
  async reset(email, code, password) {
    return cognito('ConfirmForgotPassword', { ClientId: clientId, Username: email.trim().toLowerCase(),
      ConfirmationCode: code.trim(), Password: password });
  }
};
